import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import type { Citation } from "../utils/citations";

interface CitationContextValue {
  /** Session-wide citations for export / download / save. */
  citations: Citation[];
  setCitations: (citations: Citation[]) => void;
  /** Currently open sidebar citation. */
  selectedCitation: Citation | null;
  /** Sibling list from the response group that was clicked (prev/next). */
  activeCitations: Citation[];
  openCitation: (citation: Citation, siblings: Citation[]) => void;
  closeCitation: () => void;
}

const CitationContext = createContext<CitationContextValue | null>(null);

export const useCitations = (): CitationContextValue => {
  const ctx = useContext(CitationContext);
  if (!ctx)
    throw new Error("useCitations must be used within CitationProvider");
  return ctx;
};

export const CitationProvider = ({ children }: { children: ReactNode }) => {
  const [citations, setCitations] = useState<Citation[]>([]);
  const [selectedCitation, setSelectedCitation] = useState<Citation | null>(
    null,
  );
  const [activeCitations, setActiveCitations] = useState<Citation[]>([]);

  const openCitation = useCallback(
    (citation: Citation, siblings: Citation[]) => {
      setSelectedCitation(citation);
      setActiveCitations(siblings);
    },
    [],
  );

  const closeCitation = useCallback(() => {
    setSelectedCitation(null);
    setActiveCitations([]);
  }, []);

  return (
    <CitationContext.Provider
      value={{
        citations,
        setCitations,
        selectedCitation,
        activeCitations,
        openCitation,
        closeCitation,
      }}
    >
      {children}
    </CitationContext.Provider>
  );
};
